import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { from, Observable, throwError, BehaviorSubject } from 'rxjs';
import { SearchResult } from '../interfaces/searchResult.interface';
import { ToastrService } from 'ngx-toastr';
import { DiamondService } from './diamond.service';
import { Timestamp } from 'firebase/firestore';
import axios, { AxiosResponse } from 'axios';
import { map, catchError, first } from 'rxjs/operators';
import { Firestore, collection, doc, getDoc, getDocs } from '@angular/fire/firestore';

interface AIConfig {
  openai: {
    apiKey: string;
    model: string;
    systemPrompt: string;
  };
  gemini: {
    apiKey: string;
    model: string;
  };
  prompts: {
    medicalAdvice: string;
    comprehensiveAnalysis: string; // Thêm prompt mới
    diagnosisTemplate: string;     // Có thể thêm thêm các prompt khác
  };
}


@Injectable({
  providedIn: 'root'
})
export class OpenAIService {
  private apiUrl = 'https://api.openai.com/v1/chat/completions';
  private apiUrlG = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-flash:generateContent';
  
  // Cấu hình mặc định (không chứa API key và prompt)
  private config: AIConfig = {
    openai: {
      apiKey: '',
      model: 'gpt-4o-mini',
      systemPrompt: ""
    },
    gemini: {
      apiKey: '',
      model: 'gemini-1.5-flash'
    },
    prompts: {
      medicalAdvice: '',
      comprehensiveAnalysis: '', // Thêm field mới
      diagnosisTemplate: ''
    }
  };
  
  
  private configLoaded = false;
  private configLoading = false;
  private configSubject = new BehaviorSubject<boolean>(false);

  constructor(
    private http: HttpClient,
    private toastr: ToastrService,
    private diamondService: DiamondService,
    private firestore: Firestore
  ) {
    // Không làm gì trong constructor
  }

  // Phương thức để tải cấu hình khi cần
  public async loadConfig(): Promise<boolean> {
    // Nếu đã tải xong, trả về true ngay lập tức
    if (this.configLoaded) return true;
    
    // Nếu đang tải, đợi cho đến khi tải xong
    if (this.configLoading) {
      return new Promise<boolean>((resolve) => {
        this.configSubject.pipe(first(loaded => loaded)).subscribe(() => {
          resolve(true);
        });
      });
    }
    
    this.configLoading = true;
    
    try {
      console.log('Loading AI configuration from Firestore...');
      
      // Tải toàn bộ collection một lần
      const configCollectionRef = collection(this.firestore, 'ai-config');
      const configSnapshot = await getDocs(configCollectionRef);
      
      // Xử lý từng document trong collection
      configSnapshot.forEach(doc => {
        const data = doc.data();
        const docId = doc.id;
        
        if (docId === 'openai-settings') {
          if (data.apiKey) this.config.openai.apiKey = data.apiKey;
          if (data.model) this.config.openai.model = data.model;
          if (data.systemPrompt) this.config.openai.systemPrompt = data.systemPrompt;
          console.log('OpenAI configuration loaded successfully');
        } 
        else if (docId === 'gemini-settings') {
          if (data.apiKey) this.config.gemini.apiKey = data.apiKey;
          if (data.model) this.config.gemini.model = data.model;
          console.log('Gemini configuration loaded successfully');
        }
        else if (docId === 'prompts') {
          if (data.medicalAdvice) this.config.prompts.medicalAdvice = data.medicalAdvice;
          if (data.comprehensiveAnalysis) this.config.prompts.comprehensiveAnalysis = data.comprehensiveAnalysis;
          if (data.diagnosisTemplate) this.config.prompts.diagnosisTemplate = data.diagnosisTemplate;
          console.log('Prompt templates loaded successfully');
        }        
      });
      
      this.configLoaded = true;
      this.configLoading = false;
      this.configSubject.next(true);
      
      // Kiểm tra xem có đủ thông tin cấu hình không
      this.validateConfig();
      
      return true;
    } catch (error) {
      console.error('Error loading AI configuration:', error);
      this.configLoading = false;
      this.configSubject.next(false);
      return false;
    }
  }
  
  // Kiểm tra xem cấu hình có đầy đủ không
  private validateConfig(): void {
    if (!this.config.openai.apiKey) {
      console.warn('OpenAI API key is missing');
    }
    
    if (!this.config.gemini.apiKey) {
      console.warn('Gemini API key is missing');
    }
    
    if (!this.config.prompts.medicalAdvice) {
      console.warn('Medical advice prompt template is missing');
    }
    
    if (!this.config.openai.systemPrompt) {
      console.warn('OpenAI system prompt is missing');
    }
  }

  async processOpenAIResponse(
    prompt: string,
    userDocRef: any,
    userData: any,
    coins: number,
    useAIStudio: boolean
  ): Promise<SearchResult | null> {
    await this.loadConfig();
    
    // Kiểm tra xem cấu hình prompt đã được thiết lập chưa
    if (useAIStudio && !this.config.prompts.medicalAdvice) {
      this.toastr.error('Cấu hình prompt chưa được thiết lập', 'Lỗi cấu hình');
      return null;
    }
    
    if (!useAIStudio && (!this.config.prompts.medicalAdvice || !this.config.openai.systemPrompt)) {
      this.toastr.error('Cấu hình prompt chưa được thiết lập', 'Lỗi cấu hình');
      return null;
    }
    
    if (userData.diamonds < coins) {
      this.toastr.error('Bạn không đủ điểm!', 'Chưa lấy được hướng dẫn!');
      return null;
    }

    try {
      const response$ = useAIStudio
        ? this.getAdvanceCompletionG(prompt)
        : this.getAdvanceCompletion(prompt);

      const rawContent = await response$.toPromise();

      const success = await this.diamondService.spendDiamond(userDocRef, userData, coins);

      if (success) {
        this.toastr.success(`Bạn đã sử dụng ${coins} 🏵️!`, 'Lấy hướng dẫn thành công!');
        userData.diamonds -= coins;
      }

      const jsonMatch = rawContent?.match(/\{.*\}/s);

      if (jsonMatch) {
        const parsedData = JSON.parse(jsonMatch[0]);
        const formattedData = this.formatOutputPlanData(parsedData);
        return formattedData;
      } else {
        this.toastr.error('Không tìm thấy JSON hợp lệ trong nội dung!', 'Lỗi!');
        return null;
      }
    } catch (error) {
      this.toastr.error('Lỗi khi gọi API hoặc xử lý dữ liệu!', 'Lỗi!');
      console.error(error);
      return null;
    }
  }

  private formatOutputPlanData(result: any): SearchResult {
    if (!result) {
      this.toastr.error('Dữ liệu trả về không tồn tại!', 'Lỗi!');
      return {
        clientEmail: '',
        createdAt: Timestamp.fromDate(new Date()),
        plans: [],
        guide: [],
        lab: [],
        medications: [],
        remark: ''
      }; 
    }

    const validateArray = (data: any, key: string): string[] => {
      if (Array.isArray(data[key])) {
        return data[key].filter((item: any) => typeof item === 'string');
      }
      return [];
    };

    const validateMedications = (data: any): any[] => {
      return Array.isArray(data.medications) ? data.medications.map(med => ({
        brandName: med.brandName,
        quantity: med.quantity,
        use: med.use,
      })) : [];
    };

    return {
      createdAt: Timestamp.fromDate(new Date()),
        clientEmail: '',
        providerEmail: 'AI Generated',
        guide: validateArray(result, 'guide'),
        lab: validateArray(result, 'lab'),
        medications: validateMedications(result),
        remark: typeof result.remark === 'string' ? result.remark : null,
        diagnosis: typeof result.diagnosis === 'string' ? result.diagnosis : null,
    };
  }

  getAdvanceCompletion(prompt: string): Observable<any> {
    // Kiểm tra xem có prompt được cấu hình không
    if (!this.config.prompts.medicalAdvice || !this.config.openai.systemPrompt) {
      return throwError(() => new Error('Cấu hình prompt chưa được thiết lập'));
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.config.openai.apiKey}`
    });

    const body = {
      model: this.config.openai.model,
      messages: [
        {
          role: 'system', 
          content: this.config.openai.systemPrompt
        },
        {
          role: 'user',
          content: this.config.prompts.medicalAdvice.replace('${prompt}', prompt)
        }
      ],
    };

    return this.http.post<any>(this.apiUrl, body, { headers }).pipe(
      map((response) => response?.choices[0]?.message?.content || ''),
      catchError((error) => {
        console.error('Error calling OpenAI API:', error);
        throw new Error('Failed to process AI response.');
      })
    );
  }

  getAdvanceCompletionG(inputData: string): Observable<any> {
    // Kiểm tra xem có prompt được cấu hình không
    if (!this.config.prompts.medicalAdvice) {
      return throwError(() => new Error('Cấu hình prompt chưa được thiết lập'));
    }
    
    const prompt = this.config.prompts.medicalAdvice.replace('${prompt}', inputData);

    const payload = {
      contents: [
        {
          parts: [
            {
              text: prompt,
            },
          ],
        },
      ],
    };

    const apiUrlWithKey = `${this.apiUrlG}?key=${this.config.gemini.apiKey}`;

    const axiosPromise: Promise<AxiosResponse<any>> = axios.post(apiUrlWithKey, payload, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return from(axiosPromise).pipe(
      map((response) => response?.data?.candidates[0]?.content?.parts[0]?.text || ''),
      catchError((error) => {
        console.error('Error calling AI Studio API:', error);
        throw new Error('Failed to process AI response.');
      })
    );
  }

  async processComprehensiveAnalysis(
    prompt: string,
    userDocRef: any,
    userData: any,
    coins: number,
    useAIStudio: boolean
  ): Promise<any | null> {
    await this.loadConfig();
    
    if (!this.config.prompts.comprehensiveAnalysis) {
      this.toastr.error('Cấu hình prompt phân tích toàn diện chưa được thiết lập', 'Lỗi cấu hình');
      return null;
    }
    
    if (userData.diamonds < coins) {
      this.toastr.error('Bạn không đủ điểm!', 'Chưa lấy được phân tích!');
      return null;
    }
  
    try {
      const response$ = useAIStudio
        ? this.getComprehensiveAnalysisG(prompt)
        : this.getComprehensiveAnalysisOpenAI(prompt);
  
      const rawContent = await response$.toPromise();
  
      const success = await this.diamondService.spendDiamond(userDocRef, userData, coins);
  
      if (success) {
        this.toastr.success(`Bạn đã sử dụng ${coins} 🏵️!`, 'Phân tích thành công!');
        userData.diamonds -= coins;
      }
  
      const jsonMatch = rawContent?.match(/\{.*\}/s);
  
      if (jsonMatch) {
        const parsedData = JSON.parse(jsonMatch[0]);
        return this.formatComprehensiveAnalysisData(parsedData);
      } else {
        this.toastr.error('Không tìm thấy JSON hợp lệ trong nội dung!', 'Lỗi!');
        return null;
      }
    } catch (error) {
      this.toastr.error('Lỗi khi gọi API hoặc xử lý dữ liệu!', 'Lỗi!');
      console.error(error);
      return null;
    }
  }

  getComprehensiveAnalysisOpenAI(prompt: string): Observable<any> {
    if (!this.config.prompts.comprehensiveAnalysis || !this.config.openai.systemPrompt) {
      return throwError(() => new Error('Cấu hình prompt chưa được thiết lập'));
    }
  
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.config.openai.apiKey}`
    });
  
    const body = {
      model: this.config.openai.model,
      messages: [
        {
          role: 'system', 
          content: this.config.openai.systemPrompt
        },
        {
          role: 'user',
          content: this.config.prompts.comprehensiveAnalysis.replace('${prompt}', prompt)
        }
      ],
    };
  
    return this.http.post<any>(this.apiUrl, body, { headers }).pipe(
      map((response) => response?.choices[0]?.message?.content || ''),
      catchError((error) => {
        console.error('Error calling OpenAI API:', error);
        throw new Error('Failed to process AI response.');
      })
    );
  }
  
  getComprehensiveAnalysisG(inputData: string): Observable<any> {
    if (!this.config.prompts.comprehensiveAnalysis) {
      return throwError(() => new Error('Cấu hình prompt chưa được thiết lập'));
    }
    
    const prompt = this.config.prompts.comprehensiveAnalysis.replace('${prompt}', inputData);
  
    const payload = {
      contents: [
        {
          parts: [
            {
              text: prompt,
            },
          ],
        },
      ],
    };
  
    const apiUrlWithKey = `${this.apiUrlG}?key=${this.config.gemini.apiKey}`;
  
    const axiosPromise: Promise<AxiosResponse<any>> = axios.post(apiUrlWithKey, payload, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  
    return from(axiosPromise).pipe(
      map((response) => response?.data?.candidates[0]?.content?.parts[0]?.text || ''),
      catchError((error) => {
        console.error('Error calling AI Studio API:', error);
        throw new Error('Failed to process AI response.');
      })
    );
  }

  private formatComprehensiveAnalysisData(result: any): SearchResult {
    if (!result) {
      this.toastr.error('Dữ liệu trả về không tồn tại!', 'Lỗi!');
      return {
        clientEmail: '',
        createdAt: Timestamp.fromDate(new Date()),
        guide: [],
        healthAssessment: null,
        progressTrend: null,
        nextAppointment: null,
        specialNotes: null,
        diagnosis: null,
        remark: null,
        aiAnalysisDate: Timestamp.fromDate(new Date()),
        analysisVersion: '1.0'
      };
    }
  
    return {
      createdAt: Timestamp.fromDate(new Date()),
      clientEmail: '',
      providerEmail: 'AI Comprehensive Analysis',
      guide: Array.isArray(result.guide) ? result.guide.filter(item => typeof item === 'string') : [],
      diagnosis: typeof result.diagnosis === 'string' ? result.diagnosis : null,
      remark: typeof result.remark === 'string' ? result.remark : null,
      
      // Các trường mới cho comprehensive analysis
      healthAssessment: typeof result.healthAssessment === 'string' ? result.healthAssessment : null,
      progressTrend: typeof result.progressTrend === 'string' ? result.progressTrend : null,
      nextAppointment: typeof result.nextAppointment === 'string' ? result.nextAppointment : null,
      specialNotes: typeof result.specialNotes === 'string' ? result.specialNotes : null,
      aiAnalysisDate: Timestamp.fromDate(new Date()),
      analysisVersion: '1.0'
    };
  }
  
  
  
  
}
